import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationBell from "@/components/NotificationBell";

// Desktop "pill" button style
const pillClass = ({ isActive }) =>
    `op-label px-3 py-1.5 border transition-all duration-150 whitespace-nowrap text-[11px] ${
        isActive
            ? "border-t2bb6ff text-tffffff bg-t2bb6ff/10"
            : "border-t22252a text-tc9ccd3 hover:border-t2bb6ff/60 hover:text-tffffff hover:bg-t0e1014"
    }`;

const adminPillClass = ({ isActive }) =>
    `op-label px-3 py-1.5 border transition-all duration-150 whitespace-nowrap text-[11px] ${
        isActive
            ? "border-tff7a1a text-tffffff bg-tff7a1a/15"
            : "border-tff7a1a/40 text-tff7a1a hover:bg-tff7a1a/10 hover:border-tff7a1a"
    }`;

const mobileLinkClass = ({ isActive }) =>
    `op-label block w-full px-4 py-4 border-l-2 transition-colors duration-150 text-base ${
        isActive
            ? "border-t2bb6ff text-tffffff bg-t2bb6ff/5"
            : "border-transparent text-tc9ccd3 hover:bg-t1a1d24"
    }`;

export default function Header() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    // Solo gli utenti approvati (attivi) vedono la navigazione del sito.
    const canSeeSite = !!(user && user.status === "active");
    const showAntidoping = !!(user && (user.team_id || user.role === "admin"));
    const [adCount, setAdCount] = useState(0);

    /* eslint-disable */
    useEffect(() => {
        if (!canSeeSite || !showAntidoping) { setAdCount(0); return; }
        let active = true;
        api.get("/antidoping/count")
            .then(({ data }) => { if (active) setAdCount(data?.count || 0); })
            .catch(() => {});
        return () => { active = false; };
    }, [canSeeSite, showAntidoping, loc.pathname]);
    /* eslint-enable */

    /* eslint-disable */
    useEffect(() => { setOpen(false); setSearchOpen(false); }, [loc.pathname]);
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);
    /* eslint-enable */

    const onLogout = async () => {
        await logout();
        setOpen(false);
        nav("/");
    };

    return (
        <header
            className="op-header sticky top-0 z-40 border-b border-t22252a bg-t090a0c/95 backdrop-blur"
            data-testid="site-header"
        >
            {/* TOP ROW: brand + user/auth */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
                <div className="flex items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-1" data-testid="nav-arrows">
                        <button
                            type="button"
                            onClick={() => nav(-1)}
                            aria-label="Pagina precedente"
                            title="Indietro"
                            className="w-8 h-8 flex items-center justify-center border border-t22252a text-tc9ccd3 hover:border-t2bb6ff/60 hover:text-tffffff hover:bg-t0e1014 transition-colors duration-150"
                            data-testid="nav-back-btn"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => nav(1)}
                            aria-label="Pagina successiva"
                            title="Avanti"
                            className="w-8 h-8 flex items-center justify-center border border-t22252a text-tc9ccd3 hover:border-t2bb6ff/60 hover:text-tffffff hover:bg-t0e1014 transition-colors duration-150"
                            data-testid="nav-forward-btn"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                    <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                        <img
                            src="/op-logo.png"
                            alt="Original PES"
                            className="w-10 h-10 object-contain drop-shadow-[0_0_8px_rgba(43,182,255,0.35)]"
                        />
                        <div className="leading-none">
                            <div className="op-heading text-tffffff text-lg tracking-wide">ORIGINAL PES</div>
                            <div className="op-label text-[10px] mt-1" style={{ color: "rgb(var(--tc-ff7a1a))" }}>STAGIONE 25</div>
                        </div>
                    </Link>
                </div>

                {/* Desktop global search (solo utenti approvati) */}
                {canSeeSite && (
                    <div className="hidden md:block flex-1 max-w-sm mx-6" data-testid="header-search-desktop">
                        <GlobalSearch />
                    </div>
                )}

                {/* Desktop user block */}
                <div className="hidden md:flex items-center gap-3">
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label={theme === "light" ? "Attiva tema scuro" : "Attiva tema chiaro"}
                        title={theme === "light" ? "Tema scuro" : "Tema chiaro"}
                        className="w-9 h-9 flex items-center justify-center border border-t22252a text-tc9ccd3 hover:border-t2bb6ff/60 hover:text-t2bb6ff transition-colors duration-150"
                        data-testid="theme-toggle-desktop"
                    >
                        {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    </button>
                    {user ? (
                        <>
                            {canSeeSite && <NotificationBell />}
                            <div className="flex flex-col items-end leading-tight">
                                <span className="text-sm text-tffffff" data-testid="user-name">{user.name}</span>
                                <span className="op-label text-[10px]">
                                    <span style={{ color: user.role === "admin" ? "rgb(var(--tc-ff7a1a))" : "rgb(var(--tc-2bb6ff))" }}>{user.role}</span>
                                    <span className="text-t5c6370"> · {user.status}</span>
                                </span>
                            </div>
                            <button onClick={onLogout} className="op-btn op-btn-ghost" data-testid="logout-btn">Logout</button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="op-btn op-btn-ghost" data-testid="header-login-link">Login</Link>
                            <Link to="/signup" className="op-btn op-btn-primary" data-testid="header-signup-link">Iscriviti</Link>
                        </>
                    )}
                </div>

                {/* Mobile actions: search + hamburger */}
                <div className="flex items-center gap-2 md:hidden">
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label={theme === "light" ? "Attiva tema scuro" : "Attiva tema chiaro"}
                        className="w-10 h-10 flex items-center justify-center border border-t22252a hover:border-t2bb6ff transition-colors"
                        data-testid="theme-toggle-mobile"
                    >
                        {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                    </button>
                    {canSeeSite && (
                        <button
                            onClick={() => setSearchOpen((s) => !s)}
                            className="w-10 h-10 flex items-center justify-center border border-t22252a hover:border-t2bb6ff transition-colors"
                            aria-label="Cerca"
                            data-testid="mobile-search-toggle"
                        >
                            <span className="text-base">🔍</span>
                        </button>
                    )}
                    <button
                        onClick={() => setOpen(!open)}
                        className="w-10 h-10 flex flex-col items-center justify-center gap-1.5 border border-t22252a hover:border-t2bb6ff transition-colors"
                        aria-label={open ? "Chiudi menu" : "Apri menu"}
                        data-testid="mobile-menu-toggle"
                    >
                        <span className={`block w-5 h-0.5 bg-tffffff transition-transform duration-200 ${open ? "translate-y-2 rotate-45" : ""}`} />
                        <span className={`block w-5 h-0.5 bg-tffffff transition-opacity duration-200 ${open ? "opacity-0" : ""}`} />
                        <span className={`block w-5 h-0.5 bg-tffffff transition-transform duration-200 ${open ? "-translate-y-2 -rotate-45" : ""}`} />
                    </button>
                </div>
            </div>

            {/* Mobile search row */}
            {canSeeSite && searchOpen && (
                <div className="md:hidden px-4 pb-3 pt-1 border-b border-t22252a bg-t090a0c" data-testid="mobile-search-row">
                    <GlobalSearch autoFocus onNavigate={() => setSearchOpen(false)} />
                </div>
            )}

            {/* SECOND ROW: navigation pills (desktop only) — solo utenti approvati */}
            {canSeeSite && (
            <div className="hidden md:block border-t border-t16181c">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center gap-2">
                    {/* Group: Torneo */}
                    <div className="flex items-center gap-1.5">
                        <span className="op-label text-[9px] text-t5c6370 mr-1">TORNEO</span>
                        <NavLink to="/" end className={pillClass} data-testid="nav-home">Home</NavLink>
                        <NavLink to="/squadre" className={pillClass} data-testid="nav-squadre">Squadre</NavLink>
                        <NavLink to="/campionati" className={pillClass} data-testid="nav-campionati">Campionati</NavLink>
                        <NavLink to="/coppe" className={pillClass} data-testid="nav-coppe">Coppe</NavLink>
                        <NavLink to="/calendario" className={pillClass} data-testid="nav-calendario">Calendario</NavLink>
                        <NavLink to="/albo" className={pillClass} data-testid="nav-albo">Albo d&apos;Oro</NavLink>
                        <NavLink to="/ranking" className={pillClass} data-testid="nav-ranking">Ranking</NavLink>
                        <NavLink to="/albo-premi" className={pillClass} data-testid="nav-albo-premi">Albo Premi</NavLink>
                        <NavLink to="/giocatori" className={pillClass} data-testid="nav-giocatori">Giocatori</NavLink>
                        {user && (
                            <Link
                                to="/giocatori?favorites_only=1"
                                className="op-label px-3 py-1.5 border border-te8ff00/40 text-te8ff00 hover:bg-te8ff00/10 hover:border-te8ff00 transition-all duration-150 whitespace-nowrap text-[11px]"
                                data-testid="nav-preferiti"
                            >
                                ★ Preferiti
                            </Link>
                        )}
                        <NavLink to="/disciplinare" className={pillClass} data-testid="nav-disciplinare">Disciplinare</NavLink>
                    </div>

                    <span className="w-px h-5 bg-t22252a mx-1" />

                    {/* Group: Gestione */}
                    <div className="flex items-center gap-1.5">
                        <span className="op-label text-[9px] text-t5c6370 mr-1">GESTIONE</span>
                        <NavLink to="/mercato" className={pillClass} data-testid="nav-mercato">Mercato</NavLink>
                        <NavLink to="/bilancio" className={pillClass} data-testid="nav-bilancio">Bilancio</NavLink>
                        {user && (user.team_id || user.role === "admin") && (
                            <NavLink to="/antidoping" className={pillClass} data-testid="nav-antidoping">
                                🧪 Antidoping
                                {adCount > 0 && (
                                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-t3fd07a text-black text-[9px] font-bold align-middle" data-testid="nav-antidoping-badge">
                                        {adCount}
                                    </span>
                                )}
                            </NavLink>
                        )}
                    </div>

                    <span className="w-px h-5 bg-t22252a mx-1" />

                    {/* Personal: my team */}
                    {user && user.team_id && (
                        <NavLink
                            to={`/squadre/${user.team_id}`}
                            className={({ isActive }) =>
                                `op-label px-3 py-1.5 border transition-all duration-150 whitespace-nowrap text-[11px] ${
                                    isActive
                                        ? "border-te8ff00 text-te8ff00 bg-te8ff00/10"
                                        : "border-te8ff00/40 text-te8ff00 hover:bg-te8ff00/10 hover:border-te8ff00"
                                }`
                            }
                            data-testid="nav-my-team"
                        >
                            ★ La mia squadra
                        </NavLink>
                    )}

                    <NavLink to="/regolamento" className={pillClass} data-testid="nav-regolamento">Regolamento</NavLink>

                    {/* Admin section pushed right */}
                    {user && user.role === "admin" && (
                        <div className="ml-auto flex items-center gap-1.5">
                            <span className="op-label text-[9px] text-t5c6370 mr-1">CDP</span>
                            <NavLink to="/admin" className={adminPillClass} data-testid="nav-admin">Admin</NavLink>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Mobile drawer (unchanged) */}
            {open && (
                <div
                    className="md:hidden fixed left-0 right-0 top-16 bg-t090a0c z-50 overflow-y-auto"
                    style={{ height: "calc(100vh - 4rem)" }}
                    data-testid="mobile-menu-drawer"
                >
                    <nav className="py-4 border-b border-t22252a">
                        {canSeeSite ? (
                            <>
                                <NavLink to="/" end className={mobileLinkClass} data-testid="mobile-nav-home">Home</NavLink>
                                {user && user.team_id && (
                                    <NavLink to={`/squadre/${user.team_id}`} className={mobileLinkClass} data-testid="mobile-nav-my-team">La mia squadra</NavLink>
                                )}
                                <NavLink to="/squadre" className={mobileLinkClass} data-testid="mobile-nav-squadre">Squadre</NavLink>
                                <NavLink to="/campionati" className={mobileLinkClass} data-testid="mobile-nav-campionati">Campionati</NavLink>
                                <NavLink to="/coppe" className={mobileLinkClass} data-testid="mobile-nav-coppe">Coppe</NavLink>
                                <NavLink to="/calendario" className={mobileLinkClass} data-testid="mobile-nav-calendario">Calendario</NavLink>
                                <NavLink to="/albo" className={mobileLinkClass} data-testid="mobile-nav-albo">Albo d&apos;Oro</NavLink>
                                <NavLink to="/ranking" className={mobileLinkClass} data-testid="mobile-nav-ranking">Ranking</NavLink>
                                <NavLink to="/albo-premi" className={mobileLinkClass} data-testid="mobile-nav-albo-premi">Albo Premi</NavLink>
                                <NavLink to="/giocatori" className={mobileLinkClass} data-testid="mobile-nav-giocatori">Giocatori</NavLink>
                                {user && (
                                    <NavLink to="/giocatori?favorites_only=1" className={mobileLinkClass} data-testid="mobile-nav-preferiti">★ I miei preferiti</NavLink>
                                )}
                                <NavLink to="/disciplinare" className={mobileLinkClass} data-testid="mobile-nav-disciplinare">Disciplinare</NavLink>
                                <NavLink to="/mercato" className={mobileLinkClass} data-testid="mobile-nav-mercato">Mercato</NavLink>
                                <NavLink to="/bilancio" className={mobileLinkClass} data-testid="mobile-nav-bilancio">Bilancio</NavLink>
                                {showAntidoping && (
                                    <NavLink to="/antidoping" className={mobileLinkClass} data-testid="mobile-nav-antidoping">
                                        🧪 Antidoping
                                        {adCount > 0 && (
                                            <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-t3fd07a text-black text-[9px] font-bold align-middle" data-testid="mobile-nav-antidoping-badge">
                                                {adCount}
                                            </span>
                                        )}
                                    </NavLink>
                                )}
                                <NavLink to="/regolamento" className={mobileLinkClass} data-testid="mobile-nav-regolamento">Regolamento</NavLink>
                                {user && user.role === "admin" && (
                                    <NavLink to="/admin" className={mobileLinkClass} data-testid="mobile-nav-admin">Admin</NavLink>
                                )}
                            </>
                        ) : (
                            <div className="px-4 py-3 op-mono text-xs text-t8f95a3">
                                Accedi o iscriviti per vedere il torneo.
                            </div>
                        )}
                    </nav>

                    <div className="p-4">
                        {user ? (
                            <div className="op-card p-4">
                                <div className="op-label text-[10px]">
                                    <span style={{ color: user.role === "admin" ? "rgb(var(--tc-ff7a1a))" : "rgb(var(--tc-2bb6ff))" }}>{user.role}</span>
                                    <span className="text-t5c6370"> · {user.status}</span>
                                </div>
                                <div className="text-tffffff font-semibold text-lg mt-1">{user.name}</div>
                                <div className="op-mono text-xs text-t8f95a3 mb-4">{user.email}</div>
                                <button onClick={onLogout} className="op-btn op-btn-ghost w-full" data-testid="mobile-logout-btn">Logout</button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Link to="/login" className="op-btn op-btn-ghost w-full" data-testid="mobile-login-link">Login</Link>
                                <Link to="/signup" className="op-btn op-btn-primary w-full" data-testid="mobile-signup-link">Iscriviti</Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
